import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { ChapterHead } from "./AboutSection";

const CATEGORIES = ["Tutti", "Label & Packaging", "Branding", "UI/UX", "Poster & Adv"];

const PROJECTS = [
  {
    id: "rosamore",
    title: "Rosamore — Prosecco Doc",
    category: "Label & Packaging",
    year: "2026",
    image: "/assets/f-rosamore.jpg",
    url: "https://www.behance.net/gallery/257118619/ROSAMORE-Prosecco-Doc",
  },
  {
    id: "massa",
    title: "Massa — Pizzeria (Rebranding)",
    category: "Branding",
    year: "2024",
    image: "/assets/f-massa.png",
    url: "https://www.behance.net/gallery/192973133/MASSA-Pizzeria-%28Rebranding%29",
  },
  {
    id: "punk",
    title: "Punk is not Dead — Deck + Beer",
    category: "Label & Packaging",
    year: "2024",
    image: "/assets/f-punk.png",
    url: "https://www.behance.net/gallery/210353445/Punk-is-not-Dead-Deck-Beer",
  },
  {
    id: "skaldfifl",
    title: "Skáldfifl — Grafica in lattina",
    category: "Label & Packaging",
    year: "2023",
    image: "/assets/f-skaldfifl.png",
    url: "https://www.behance.net/gallery/165863381/Skaldfifl-proposta-label-%28Grafica-in-lattina%29",
  },
  {
    id: "palmieri",
    title: "Marchio Palmieri — Vinoria",
    category: "Branding",
    year: "2022",
    image: "/assets/f-palmieri.png",
    url: "https://www.behance.net/gallery/141687989/Marchio-Palmieri-label-Vinoria-vino-storia",
  },
  {
    id: "ca-del-doge",
    title: "Cà del Doge — Prosecco",
    category: "Label & Packaging",
    year: "2024",
    image: "/assets/p-cadeldoge.png",
    url: "https://www.behance.net/gallery/253142655/Ca-del-Doge-Prosecco",
  },
  {
    id: "savanne",
    title: "Savanne & Apoteosi — Label",
    category: "Label & Packaging",
    year: "2024",
    image: "/assets/p-savanne.png",
    url: "https://www.behance.net/gallery/246152025/Savanne-Apoteosi-label-prosecco",
  },
  {
    id: "alfieri",
    title: "Alfieri — Pasticceria d'Autore",
    category: "Branding",
    year: "2024",
    image: "/assets/p-alfieri.png",
    url: "https://www.behance.net/gallery/237448025/Alfieri-Pasticceria-dautore",
  },
  {
    id: "kakao",
    title: "Kakao — Beauty Branding",
    category: "Branding",
    year: "2023",
    image: "/assets/p-kakao.png",
    url: "https://www.behance.net/gallery/219902897/Kakao-Beauty-Branding",
  },
  {
    id: "nova",
    title: "N O V A — Visual Identity",
    category: "Branding",
    year: "2023",
    image: "/assets/p-nova.png",
    url: "https://www.behance.net/gallery/207518443/N-O-V-A",
  },
  {
    id: "flixbus",
    title: "Flixbus — Redesign App",
    category: "UI/UX",
    year: "2023",
    image: "/assets/p-flixbus.png",
    url: "https://www.behance.net/gallery/226851339/FLIXBUS-Redesign-App-UIUX",
  },
  {
    id: "mon-reve",
    title: "Mon Rêve — Summer Festival",
    category: "Poster & Adv",
    year: "2023",
    image: "/assets/p-monreve.png",
    url: "https://www.behance.net/gallery/218101615/Mon-Reve-Summer-Festival-2023-(Outdoor-adv)",
  },
  {
    id: "movie-poster",
    title: "Movie Poster — Collection",
    category: "Poster & Adv",
    year: "2023",
    image: "/assets/p-movieposter.png",
    url: "https://www.behance.net/gallery/205959741/Movie-Poster-Collection",
  },
];

export default function PortfolioSection() {
  const [filter, setFilter] = useState("Tutti");
  const visible =
    filter === "Tutti" ? PROJECTS : PROJECTS.filter((p) => p.category === filter);

  return (
    <section id="portfolio" data-testid="portfolio-section" className="relative">
      <div className="pointer-events-none absolute right-10 top-28 hidden h-12 w-12 bg-magenta-brand lg:block" />
      <div className="mx-auto max-w-[1400px] px-5 py-24 sm:px-8 lg:py-32">
        <ChapterHead number="03" title="Progetti selezionati" testid="portfolio-heading" />

        <div className="mb-12 flex flex-wrap items-center gap-3" data-testid="portfolio-filters">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              data-testid={`portfolio-filter-${c.toLowerCase().replace(/[^a-z]/g, "-")}`}
              onClick={() => setFilter(c)}
              className={`border px-4 py-2 font-mono text-xs tracking-wider transition-colors duration-300 ${
                filter === c
                  ? "border-ink bg-ink text-paper"
                  : "border-neutral-300 text-neutral-600 hover:border-ink hover:text-ink"
              }`}
            >
              {c.toUpperCase()}
            </button>
          ))}
          <a
            href="https://www.behance.net/peppefasino"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="portfolio-behance-all"
            className="ml-auto hidden items-center gap-2 font-mono text-xs tracking-wider text-blue-brand hover:text-magenta-brand sm:flex"
          >
            VEDI TUTTO SU BEHANCE
            <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>

        <motion.div layout className="grid grid-cols-1 gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-3">
          <AnimatePresence mode="popLayout">
            {visible.map((p, i) => (
              <motion.a
                layout
                key={p.id}
                href={p.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`portfolio-card-${p.id}`}
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.55, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                className="group block"
              >
                <div className="relative aspect-[4/3] overflow-hidden border border-neutral-200 bg-white">
                  <img
                    src={p.image}
                    alt={p.title}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                  <div className="absolute inset-0 flex items-end justify-between bg-ink/0 p-5 transition-colors duration-500 group-hover:bg-ink/25">
                    <span className="translate-y-3 bg-paper px-3 py-1.5 font-mono text-[10px] tracking-wider opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                      CASE STUDY ↗
                    </span>
                    <span className="h-8 w-8 translate-y-3 rounded-tl-full bg-cyan-brand opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100" />
                  </div>
                </div>
                <div className="mt-4 flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-display text-lg font-bold leading-tight tracking-tight transition-colors duration-300 group-hover:text-blue-brand">
                      {p.title}
                    </h3>
                    <p className="mt-1.5 font-mono text-[10px] uppercase tracking-wider text-neutral-500">
                      {p.category} — {p.year}
                    </p>
                  </div>
                  <ArrowUpRight className="mt-1 h-5 w-5 shrink-0 text-neutral-300 transition-all duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-magenta-brand" />
                </div>
              </motion.a>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
}
